/**
 * InventoryManager v2
 * Props:
 *  - stores: array of {id,name} for store selector (obligatoire)
 * Usage:
 *  <InventoryManager stores={[{id:'A', name:'Magasin A'}]} />
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useIsFetching,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import InForm from './forms/InForm';
import OutForm from './forms/OutForm';
import MoveForm from './forms/MoveForm';
import ReplaceForm from './forms/ReplaceForm';
import AdjustForm from './forms/AdjustForm';
import ReserveForm from './forms/ReserveForm';
import ReleaseForm from './forms/ReleaseForm';
import WriteOffForm from './forms/WriteOffForm';
import BalanceTable from './grids/BalanceTable';
import HistoryTable from './grids/HistoryTable';
import AgingTable from './grids/AgingTable';
import FlowChart from './grids/FlowChart';
import { fr } from '../i18n/fr';
import ProductSelectionModal from './ProductSelectionModal';
import AddInternalProductDialog from './dialogs/AddInternalProductDialog';
import { ProductSelectionItem } from '../types';
import { generateBarcodeFromValue } from '../../../components/barcodeHelper';
import { formatDate, formatPrice } from '../utils/formatters';
import { fetchStockMovementsByStoreAndState } from '../api/queries';
import type { StockMovementWithProductName } from '../api/dto';

const TEN_MINUTES = 10 * 60 * 1000;

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: TEN_MINUTES,
      refetchInterval: TEN_MINUTES,
      refetchOnWindowFocus: false,
    },
  },
});

const tabs = [
  { key: 'in', label: 'Entrée' },
  { key: 'out', label: 'Sortie' },
  { key: 'move', label: 'Transfert' },
  { key: 'replace', label: 'Remplacement' },
  { key: 'adjust', label: 'Ajustement' },
  { key: 'reserve', label: 'Réservation' },
  { key: 'release', label: 'Libération' },
  { key: 'writeoff', label: 'Dépréciation' },
] as const;

interface Props {
  stores: Array<{ id: string; name: string }>;
  canPerform?: Record<string, boolean>;
}

const InventoryManagerContent: React.FC<Props> = ({ stores, canPerform }) => {
  const queryClient = useQueryClient();
  const isRefreshing = useIsFetching({ queryKey: ['inventory'] }) > 0;
  const [storeId, setStoreId] = useState<string>(stores[0]?.id || '');
  const [productId, setProductId] = useState<string>('');
  const [active, setActive] = useState<(typeof tabs)[number]['key']>('in');
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductSelectionItem | null>(
    null
  );
  const [printDialogProduct, setPrintDialogProduct] = useState<
    ProductSelectionItem | null
  >(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [lostExportDialogOpen, setLostExportDialogOpen] = useState(false);
  const [lostExportLoading, setLostExportLoading] = useState(false);
  const [lostMovements, setLostMovements] = useState<StockMovementWithProductName[]>([]);
  const missingBarcodeLabel = fr.missingBarcode ?? 'Aucun code-barres disponible';
  const productLabelFallback = fr.product ?? 'Produit';

  const handleRefresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    void queryClient.refetchQueries({ queryKey: ['inventory'], type: 'active' });
  }, [queryClient]);

  const handleClosePrintDialog = useCallback(() => {
    setPrintDialogProduct(null);
  }, []);

  const handlePrintLabel = useCallback(
    (product: ProductSelectionItem, options?: { showPrice?: boolean }) => {
      if (typeof window === 'undefined') {
        return;
      }

      const escapeHtml = (value: string) =>
        value
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

      const productName = product.name?.trim() || productLabelFallback;
      const showPrice = options?.showPrice ?? true;
      const formattedPrice = showPrice
        ? formatPrice(product.regularPrice ?? product.price)
        : '';
      const barcodeSvg = product.barcode
        ? generateBarcodeFromValue(product.barcode)
        : '';
      const barcodeText = product.barcode?.toString().trim() || missingBarcodeLabel;

      const printWindow = window.open('', '_blank', 'width=600,height=400');
      if (!printWindow) {
        return;
      }

      const doc = printWindow.document;
      const priceMarkup = formattedPrice
        ? `<div class="price">${escapeHtml(formattedPrice)}</div>`
        : '';
      const barcodeMarkup = barcodeSvg
        ? `<div class="barcode">${barcodeSvg}</div>`
        : `<div class="barcode barcode--text">${escapeHtml(barcodeText)}</div>`;

      doc.open();
      doc.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Impression code-barres - ${escapeHtml(productName)}</title>
    <style>
      @page { size: 50mm 25mm; margin: 0; }
      html, body {
        margin: 0;
        padding: 0;
        width: 50mm;
        height: 25mm;
      }
      body {
        font-family: 'Roboto', Arial, sans-serif;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
      }
      .label {
        box-sizing: border-box;
        width: 50mm;
        height: 25mm;
        padding: 2mm;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 1mm;
      }
      .name {
        font-size: 10pt;
        font-weight: 600;
        margin: 5px 0 0 0;
        line-height: 1.2;
      }
      .price {
        font-size: 9pt;
        font-weight: 500;
        margin: 0;
      }
      .barcode {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .barcode svg {
        width: 90%;
        max-width: 44mm;
        height: auto;
        max-height: 14mm;
      }
      .barcode--text {
        font-size: 8pt;
        letter-spacing: 1px;
      }
    </style>
  </head>
  <body>
    <div class="label">
      <div class="name">${escapeHtml(productName)}</div>
      ${priceMarkup}
      ${barcodeMarkup}
    </div>
  </body>
</html>`);
      doc.close();

      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 100);
    },
    [missingBarcodeLabel, productLabelFallback]
  );

  const handleConfirmPrint = useCallback(
    (showPrice: boolean) => {
      if (printDialogProduct) {
        handlePrintLabel(printDialogProduct, { showPrice });
      }
      setPrintDialogProduct(null);
    },
    [handlePrintLabel, printDialogProduct]
  );

  useEffect(() => {
    if (!stores.length) {
      setStoreId('');
      return;
    }
    if (!storeId || !stores.find((store) => store.id === storeId)) {
      setStoreId(stores[0].id);
    }
  }, [storeId, stores]);

  const selectedStoreName = useMemo(
    () => stores.find((s) => s.id === storeId)?.name ?? stores[0]?.name ?? '',
    [storeId, stores]
  );

  const effectiveProductId = selectedProduct?.id || productId;

  const handleProductPicked = useCallback(
    (product: ProductSelectionItem | null, fallbackId?: string) => {
      if (product) {
        setSelectedProduct(product);
        setProductId(product.id);
        return;
      }
      setSelectedProduct(null);
      setProductId(fallbackId ?? '');
    },
    []
  );

  const activeTab = useMemo(
    () => tabs.find((tab) => tab.key === active) ?? tabs[0],
    [active]
  );

  const formContent = useMemo(() => {
    const formProps = {
      initialProductId: effectiveProductId,
      initialProduct: selectedProduct,
      onSubmitted: () => setFormModalOpen(false),
    } as const;
    switch (active) {
      case 'in':
        return (
          <InForm
            storeId={storeId}
            initialProductId={formProps.initialProductId}
            initialProduct={formProps.initialProduct}
            onSubmitted={formProps.onSubmitted}
          />
        );
      case 'out':
        return (
          <OutForm
            storeId={storeId}
            initialProductId={formProps.initialProductId}
            initialProduct={formProps.initialProduct}
            onSubmitted={formProps.onSubmitted}
          />
        );
      case 'move':
        return (
          <MoveForm
            initialProductId={formProps.initialProductId}
            initialProduct={formProps.initialProduct}
            onSubmitted={formProps.onSubmitted}
            stores={stores}
          />
        );
      case 'replace':
        return (
          <ReplaceForm
            initialProductId={formProps.initialProductId}
            initialProduct={formProps.initialProduct}
            onSubmitted={formProps.onSubmitted}
          />
        );
      case 'adjust':
        return (
          <AdjustForm
            storeId={storeId}
            initialProductId={formProps.initialProductId}
            initialProduct={formProps.initialProduct}
            onSubmitted={formProps.onSubmitted}
          />
        );
      case 'reserve':
        return (
          <ReserveForm
            storeId={storeId}
            initialProductId={formProps.initialProductId}
            initialProduct={formProps.initialProduct}
            onSubmitted={formProps.onSubmitted}
          />
        );
      case 'release':
        return (
          <ReleaseForm
            storeId={storeId}
            initialProductId={formProps.initialProductId}
            initialProduct={formProps.initialProduct}
            onSubmitted={formProps.onSubmitted}
          />
        );
      case 'writeoff':
        return (
          <WriteOffForm
            storeId={storeId}
            initialProductId={formProps.initialProductId}
            initialProduct={formProps.initialProduct}
            onSubmitted={formProps.onSubmitted}
          />
        );
      default:
        return null;
    }
  }, [active, effectiveProductId, selectedProduct, storeId]);

  const storeEntries = useMemo(
    () => stores.map((store) => [store.id, store.name] as const),
    [stores]
  );

  const storeMap = useMemo(() => new Map(storeEntries), [storeEntries]);
  const storeRecord = useMemo(
    () => Object.fromEntries(storeEntries),
    [storeEntries]
  );

  const lostMovementRows = useMemo(
    () =>
      lostMovements.map((movement) => ({
        ...movement,
        resolvedProductName:
          movement.product_name?.trim() ||
          movement.manual?.description?.trim() ||
          movement.manual?.sku_text?.trim() ||
          movement.product_id ||
          productLabelFallback,
        formattedDate: formatDate(movement.ts),
      })),
    [lostMovements, productLabelFallback]
  );

  const handleOpenLostExportPreview = useCallback(async () => {
    if (!storeId) {
      toast.error('Veuillez sélectionner un magasin.');
      return;
    }

    setLostExportLoading(true);
    try {
      const data = await fetchStockMovementsByStoreAndState({
        storeId,
        status: 'lost',
        limit: 1000,
      });
      setLostMovements(data);
      setLostExportDialogOpen(true);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Erreur lors du chargement du stock perdu.';
      toast.error(message);
    } finally {
      setLostExportLoading(false);
    }
  }, [storeId]);

  const handleExportLostMovements = useCallback(() => {
    if (!lostMovementRows.length) {
      toast.error('Aucune ligne à exporter.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(
      lostMovementRows.map((movement) => ({
        Produit: movement.resolvedProductName,
        Quantite: movement.quantity,
        Date: movement.formattedDate,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock perdu');

    const safeStoreName = (selectedStoreName || 'magasin')
      .replace(/[\\/:*?"<>|]/g, '-')
      .trim()
      .replace(/\s+/g, '_');

    XLSX.writeFile(workbook, `stock_perdu_${safeStoreName}.xlsx`);
    toast.success('Export XLSX généré.');
  }, [lostMovementRows, selectedStoreName]);

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          background: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.08)}, ${alpha(
              theme.palette.secondary.light,
              0.1
            )})`,
          borderRadius: 3,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              select
              fullWidth
              label={fr.selectStore}
              aria-label="store-selector"
              value={storeId}
              onChange={(event) => setStoreId(event.target.value)}
              color="primary"
            >
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label={fr.description ?? fr.product}
              value={selectedProduct?.name ?? productId}
              onClick={() => setProductModalOpen(true)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    {selectedProduct && (
                      <Tooltip title={fr.clear ?? 'Effacer'}>
                        <span>
                          <IconButton onClick={() => handleProductPicked(null)}>
                            <ClearIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title={fr.search}>
                      <IconButton onClick={() => setProductModalOpen(true)}>
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setAddProductDialogOpen(true)}
              sx={{
                alignSelf: { xs: 'stretch', sm: 'flex-start' },
                minWidth: { sm: 200 },
                borderRadius: 2,
              }}
            >
              {fr.addProduct}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={
                isRefreshing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )
              }
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{
                alignSelf: { xs: 'stretch', sm: 'flex-start' },
                minWidth: { sm: 200 },
                borderRadius: 2,
              }}
            >
              {fr.refresh}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                lostExportLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
              onClick={handleOpenLostExportPreview}
              disabled={!storeId || lostExportLoading}
              sx={{
                alignSelf: { xs: 'stretch', sm: 'flex-start' },
                minWidth: { sm: 220 },
                borderRadius: 2,
              }}
            >
              Export stock perdu
            </Button>
          </Stack>
          <Tabs
            value={active}
            onChange={(
              _event,
              value: (typeof tabs)[number]['key']
            ) => setActive(value)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="secondary"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.key}
                label={tab.label}
                disabled={canPerform && canPerform[tab.key] === false}
              />
            ))}
          </Tabs>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: { xs: 'flex-start', md: 'stretch' },
            }}
          >
            {selectedStoreName && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 64,
                }}
              >
                {selectedStoreName}
              </Typography>
            )}
            <Paper
              elevation={1}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: (theme) => theme.palette.background.paper,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                width: { xs: '100%', md: selectedStoreName ? 'auto' : '100%' },
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h6" color="primary">
                  {activeTab.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fr.openMovementHelper}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setFormModalOpen(true)}
                  disabled={!storeId}
                  sx={{ borderRadius: 2 }}
                >
                  {fr.openMovement(activeTab.label)}
                </Button>
                {selectedProduct && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {selectedProduct.name ?? selectedProduct.description ?? ''}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PrintIcon fontSize="small" />}
                      onClick={() =>
                        selectedProduct && setPrintDialogProduct(selectedProduct)
                      }
                    >
                      {fr.printBarcode ?? 'Imprimer le code-barres'}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </Paper>

      <Stack spacing={3}>
        <BalanceTable
          storeId={storeId}
          stores={stores}
          selectedProductId={effectiveProductId}
          onSelectProduct={(product) => {
            handleProductPicked(product, product?.id);
            setFormModalOpen(true);
          }}
          onPrintProduct={(product) => setPrintDialogProduct(product)}
        />
        <HistoryTable
          productId={productId}
          productName={selectedProduct?.name ?? selectedProduct?.description ?? ''}
          storeName={storeMap.get(storeId) ?? storeId}
          storeNamesById={storeRecord}
        />
      </Stack>

      <AgingTable storeId={storeId} />

      <FlowChart storeId={storeId} />
      <ProductSelectionModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onConfirm={(products) => {
          const product = products[0] ?? null;
          handleProductPicked(product, product?.id);
          setProductModalOpen(false);
        }}
        selectedIds={selectedProduct?.id ? [selectedProduct.id] : productId ? [productId] : []}
      />
      <AddInternalProductDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        onCreated={(product) => {
          setAddProductDialogOpen(false);
          if (product) {
            handleProductPicked(product, product.id);
            void queryClient.invalidateQueries({ queryKey: ['inventory', 'products', 'all'] });
          }
        }}
      />
      <Dialog open={formModalOpen} onClose={() => setFormModalOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{activeTab.label}</DialogTitle>
        <DialogContent dividers>{formContent}</DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setFormModalOpen(false)}>
            {fr.cancel}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(printDialogProduct)} onClose={handleClosePrintDialog}>
        <DialogTitle>{fr.printBarcode}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            {fr.printBarcodePriceQuestion}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => handleConfirmPrint(false)}>
            {fr.no}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleConfirmPrint(true)}
            disabled={!printDialogProduct}
          >
            {fr.yes}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={lostExportDialogOpen}
        onClose={() => setLostExportDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Stock perdu à exporter</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {selectedStoreName || storeId}
            </Typography>
            {!lostMovementRows.length ? (
              <Alert severity="info">Aucun mouvement en état perdu pour ce magasin.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table stickyHeader aria-label="lost-stock-export-preview">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lostMovementRows.map((movement) => (
                      <TableRow key={movement.id || `${movement.product_id}-${movement.ts}`}>
                        <TableCell>{movement.resolvedProductName}</TableCell>
                        <TableCell align="right">{movement.quantity}</TableCell>
                        <TableCell>{movement.formattedDate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setLostExportDialogOpen(false)}>
            {fr.close}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon fontSize="small" />}
            onClick={handleExportLostMovements}
            disabled={!lostMovementRows.length}
          >
            Exporter XLSX
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const InventoryManager: React.FC<Props> = (props) => (
  <QueryClientProvider client={qc}>
    <InventoryManagerContent {...props} />
  </QueryClientProvider>
);

export default InventoryManager;
